/**
 * TableRouter – main router component for different document model table renderers
 * 
 * This component determines which specific table component to render based on the model type.
 * It acts as a router/controller that delegates to the appropriate specialized table component.
 * 
 * Features:
 * - Routes to ShippingLabelsTable for 'shipping-labels' model
 * - Routes to SafetyDatasheetsTable for 'safety-datasheets' model  
 * - Routes to DefaultTable for unsupported models or no document selected
 * - Passes through all props to the appropriate table component
 * 
 * Props expected (passed through to child components):
 * - model: determines which table component to render
 * - selectedDocument: document data with pages
 * - documentFilename: filename for title display
 * - expandedRows: expansion state
 * - toggleRow: row expansion handler
 * - anchorEl: menu state
 * - handleMenuOpen, handleMenuClose: menu handlers
 * - downloadFiles: download utility function
 * 
 * Author: Galo Alonso Peralta Espinoza  
 * Date: 04-17-2025
 */

import React from 'react'

// Import individual table components
import ShippingLabelsTable from './ShippingLabelsTable'
import SafetyDatasheetsTable from './SafetyDatasheetsTable'
import DefaultTable from './DefaultTable'

/**
 * Main Table Router Component
 * Determines which table component to render based on model and document state
 */
export default function TableRouter({ model, selectedDocument, ...props }) {
  
  // If no document is selected, show default message
  if (!selectedDocument) {
    return <DefaultTable selectedDocument={selectedDocument} model={model} />
  }

  // If document is selected but no model, show unsupported message
  if (!model) {
    return <DefaultTable selectedDocument={selectedDocument} model={model} />
  }

  // Determine which table to render based on model
  switch(model) {
    case 'shipping-labels':
      return <ShippingLabelsTable selectedDocument={selectedDocument} {...props} />
    case 'safety-datasheets':
      return <SafetyDatasheetsTable selectedDocument={selectedDocument} {...props} />
    default:
      // For unknown models, show unsupported message
      return <DefaultTable selectedDocument={selectedDocument} model={null} />
  }
} 